import { PdfChart, PdfChartProps } from "@app/components/Pdf";
import { ChartConfiguration, FontSpec } from "chart.js";
import { ChartPlugins, ChartUtils } from "@app/utils/chartUtils";
import { useTheme } from "@mui/material";
import { Image as PdfImage, Text, View } from "@react-pdf/renderer";
import { formatPercentFactor } from "@app/utils";
import { ChartDataset } from "chart.js";
import { CarPortfolioChartItem } from "@app/pages/assetAllocation/asset-allocation-types";
import { getMultiPeriodReturnComparisonChartData } from "@app/pages/assetAllocation/editPortfolioGroup/ChartMultiPeriodReturnComparison";
import { CarPdfBox } from "@app/components/PdfBox";
import { CarPdfChartLegend } from "@app/components/PdfChartLegend";
import { CarPdfChart, CarPdfChartProps } from "@app/components/PdfChart";

// const stripedBlue = new Image();
// stripedBlue.src =
//   "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQBAMAAADt3eJSAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAGUExURZS74m6c0s+RzegAAAAJcEhZcwAADsEAAA7BAbiRa+0AAAA0SURBVBjTfc3LDQAgCATRoQPov1nXOPGmXJa88AEmlaCSzQ8QCmEQdnMGENK4gXBPvL81CzKfBEEcf/J0AAAAAElFTkSuQmCC";

// const stripedGreen = new Image();
// stripedGreen.src =
//   "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQBAMAAADt3eJSAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAGUExURb7Moqu+huWQCDwAAAAJcEhZcwAADsEAAA7BAbiRa+0AAAA0SURBVBjTfc3LDQAgCATRoQPov1nXOPGmXJa88AEmlaCSzQ8QCmEQdnMGENK4gXBPvL81CzKfBEEcf/J0AAAAAElFTkSuQmCC";

// backgroundColor: ({ chart: { ctx } }) =>
//   ctx.createPattern(stripedGreen, "repeat") ?? undefined,
// ...{ backgroundLegend: stripedGreen.src },

interface MultiPeriodReturnPdfChartProps
  extends Omit<CarPdfChartProps, "type" | "options" | "data" | "plugins"> {
  data: CarPortfolioChartItem[];
  data1Id: string;
  data2Id: string;
  isWhiteContext?: boolean;
}

export const MultiPeriodReturnPdfChart = ({
  data,
  data1Id,
  data2Id,
  isWhiteContext,
  ...props
}: MultiPeriodReturnPdfChartProps) => {
  const theme = useTheme();

  const { datasets, yLabel, xLabels } = getMultiPeriodReturnComparisonChartData(
    {
      theme,
      data,
      data1Id,
      data2Id,
      forPdf: true,
    },
  );

  const font: Partial<FontSpec> = {
    size: 9,
    weight: 400,
    lineHeight: 1,
    family: theme.typography.fontFamily,
  };

  const titleFont: Partial<FontSpec> = {
    ...font,
    size: 12,
    weight: 600,
  };

  return (
    <CarPdfBox>
      <CarPdfChart
        type="bar"
        data={{ datasets }}
        options={{
          scales: {
            x: {
              type: "category",
              ticks: {
                font,
                color: theme.palette.softBlack,
                padding: 0,
              },
              grid: ChartUtils.getGridOptions({ theme, showMinMaxLines: true }),
              labels: xLabels,
            },
            y: {
              ticks: {
                font,
                color: theme.palette.softBlack,
                padding: 0,
                callback: (value) =>
                  typeof value === "number"
                    ? formatPercentFactor(value)
                    : value,
              },
              grid: ChartUtils.getGridOptions({ theme }),
              title: {
                display: true,
                text: yLabel,
                color: theme.palette.softBlack,
                font: titleFont,
              },
            },
          },
          plugins: {
            roundedBackground: ChartUtils.getRoundedBackgroundOptions({
              theme,
              isWhiteContext,
            }),
          },
        }}
        plugins={[ChartPlugins.roundedBackground]}
        {...props}
      />
      <CarPdfChartLegend
        style={{ marginTop: 16 }}
        items={datasets.map((i) => ({
          label: i.label ?? "",
          color: String(i.backgroundColor),
          isCircle: true,
        }))}
      />
    </CarPdfBox>
  );
};
